"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";

const PNL_STRUCTURE = [
  { type: "section", label: "SALES" },
  {
    type: "row",
    label: "ACTIVE PROJECT SALES (CONTRACTED)",
    key: "revActiveProjectSales",
  },
  {
    type: "row",
    label: "PIPELINE - PROJECTS IDENTIFIED",
    key: "revPipelineIdentified",
  },
  {
    type: "row",
    label: "PIPELINE - PROJECTS UNIDENTIFIED",
    key: "revPipelineUnidentified",
  },
  { type: "row", label: "PIPELINE - GAP TO BUDGET", key: "revPipelineGap" },
  { type: "row", label: "NEW BUSINESS", key: "revNewBusiness" },
  { type: "row", label: "DISCLOSED AGENTS REVENUE", key: "revDisclosedAgents" },
  { type: "row", label: "PASS THROUGH REVENUE", key: "revPassThrough" },
  { type: "total", label: "TOTAL SALES", key: "totSales" },

  { type: "section", label: "COGS COST OF GOODS SOLD" },
  {
    type: "row",
    label: "ACTIVE PROJECT COST (CONTRACTED)",
    key: "cogsActiveProject",
  },
  {
    type: "row",
    label: "PIPELINE - PROJECTS IDENTIFIED",
    key: "cogsPipelineIdentified",
  },
  {
    type: "row",
    label: "PIPELINE - PROJECTS UNIDENTIFIED",
    key: "cogsPipelineUnidentified",
  },
  { type: "row", label: "PIPELINE - GAP TO BUDGET", key: "cogsPipelineGap" },
  { type: "row", label: "NEW BUSINESS", key: "cogsNewBusiness" },
  { type: "row", label: "DISCLOSED AGENTS COGS", key: "cogsDisclosedAgents" },
  { type: "row", label: "PASS THROUGH COGS", key: "cogsPassThrough" },
  { type: "row", label: "MARGIN IMPROVEMENTS", key: "cogsMarginImprovements" },
  { type: "total", label: "TOTAL COGS", key: "totCogs" },
  {
    type: "grand_total",
    label: "EXTERNAL GROSS PROFIT",
    key: "externalGrossProfit",
  },

  { type: "metric", label: "COGS HEADCOUNT", key: "hcCogs", isCount: true },
  {
    type: "metric",
    label: "FREELANCE HEADCOUNT",
    key: "hcFreelance",
    isCount: true,
  },

  { type: "section", label: "COGS SALARIES" },
  { type: "row", label: "SALARIES COS", key: "cogsSalCos" },
  { type: "row", label: "SALARIES COS GROSS", key: "cogsSalCosGross" },
  { type: "row", label: "SALARIES COS NI", key: "cogsSalCosNi" },
  { type: "row", label: "COMMISSION & BONUSES COS", key: "cogsBonusCos" },
  {
    type: "row",
    label: "COMMISSION & BONUSES COS GROSS",
    key: "cogsBonusCosGross",
  },
  { type: "row", label: "COMMISSION & BONUSES COS NI", key: "cogsBonusCosNi" },
  { type: "row", label: "PENSION COS", key: "cogsPensionCos" },
  { type: "row", label: "DEFERRED TIME", key: "cogsDeferredTime" },
  {
    type: "row",
    label: "FREELANCE CONSULTANT",
    key: "cogsFreelanceConsultant",
  },
  { type: "total", label: "TOTAL COGS SALARIES", key: "totCogsSalaries" },
  { type: "grand_total", label: "GROSS INCOME", key: "grossIncome" },

  { type: "section", label: "OTHER INCOME" },
  { type: "row", label: "RENTAL INCOME", key: "oiRental" },
  { type: "row", label: "OTHER INCOME", key: "oiOther" },
  { type: "total", label: "TOTAL OTHER INCOME", key: "totOtherIncome" },

  { type: "section", label: "EXPENSES - OPERATIONAL" },
  {
    type: "metric",
    label: "OVERHEAD SALARIES HEADCOUNT",
    key: "hcOhSalaries",
    isCount: true,
  },
  { type: "row", label: "SALARIES OH", key: "ohSalaries" },
  { type: "row", label: "SALARIES OH GROSS", key: "ohSalariesGross" },
  { type: "row", label: "SALARIES OH NI", key: "ohSalariesNi" },
  { type: "row", label: "COMMISSION & BONUSES OH", key: "ohBonus" },
  { type: "row", label: "COMMISSION & BONUSES OH GROSS", key: "ohBonusGross" },
  { type: "row", label: "COMMISSION & BONUSES OH NI", key: "ohBonusNi" },
  { type: "row", label: "PENSION OH", key: "ohPension" },
  { type: "row", label: "HEALTHCARE & DEATH IN SERVICE", key: "ohHealthcare" },
  { type: "row", label: "STAFF ENTERTAINMENT & MOTIVATION", key: "ohStaffEnt" },
  { type: "row", label: "TRAVEL & SUBS", key: "ohTravel" },
  { type: "row", label: "TRAINING", key: "ohTraining" },
  { type: "row", label: "TEMPORARY STAFF", key: "ohTempStaff" },
  { type: "row", label: "RECRUITMENT", key: "ohRecruitment" },
  { type: "subtotal", label: "TOTAL STAFF COSTS", key: "totStaffCosts" },

  { type: "row", label: "RATES", key: "premRates" },
  { type: "row", label: "RENT", key: "premRent" },
  { type: "row", label: "SERVICE CHARGE", key: "premService" },
  { type: "row", label: "REPAIRS & RENEWALS", key: "premRepairs" },
  { type: "row", label: "HEAT, LIGHT & WATER", key: "premHeat" },
  { type: "row", label: "WAREHOUSE", key: "premWarehouse" },
  { type: "row", label: "EQUIPMENT HIRE", key: "premEquip" },
  { type: "row", label: "CLEANING", key: "premCleaning" },
  { type: "row", label: "OFFICE SUPPLIES", key: "premOfficeSup" },
  { type: "row", label: "VEHICLES", key: "premVehicles" },
  { type: "row", label: "SECURITY", key: "premSecurity" },
  { type: "subtotal", label: "TOTAL PREMISES", key: "totPremises" },

  { type: "row", label: "ACCOUNTING & TAX", key: "profAccounting" },
  { type: "row", label: "AUDIT", key: "profAudit" },
  { type: "row", label: "CONSULTANCY", key: "profConsultancy" },
  { type: "row", label: "LEGAL & PROFESSIONAL", key: "profLegal" },
  { type: "row", label: "RESEARCH & DEVELOPMENT", key: "profRnd" },
  { type: "row", label: "INSURANCE", key: "profInsurance" },
  { type: "row", label: "QUALITY & SUSTAINABILITY", key: "profQuality" },
  { type: "subtotal", label: "TOTAL PROFESSIONAL FEES", key: "totProfFees" },

  { type: "row", label: "IT COMMUNICATIONS", key: "itComms" },
  { type: "row", label: "IT CONSUMABLES", key: "itConsumables" },
  { type: "row", label: "IT SOFTWARE & LICENCES", key: "itSoftware" },
  { type: "row", label: "IT SUPPORT", key: "itSupport" },
  { type: "subtotal", label: "TOTAL IT COSTS", key: "totItCosts" },

  { type: "row", label: "SUBSCRIPTIONS", key: "othSubs" },
  { type: "row", label: "CHARITY DONATIONS", key: "othCharity" },
  { type: "row", label: "BOARD ACTIVITIES", key: "othBoard" },
  { type: "row", label: "BAD DEBT WRITE/OFF", key: "othBadDebt" },
  { type: "row", label: "MANAGEMENT FEES", key: "othMgmtFees" },
  { type: "subtotal", label: "TOTAL OTHER", key: "totOther" },

  { type: "row", label: "BUSINESS DEVELOPMENT", key: "nbDev" },
  { type: "row", label: "CLIENT ENTERTAINMENT", key: "nbClientEnt" },
  { type: "row", label: "PITCH COSTS (LOST BUSINESS)", key: "nbPitch" },
  { type: "subtotal", label: "TOTAL NEW BUSINESS COSTS", key: "totNewBiz" },

  { type: "row", label: "MARKETING COSTS", key: "smMarketing" },
  {
    type: "subtotal",
    label: "TOTAL SALES & MARKETING",
    key: "totSalesMarketing",
  },
  { type: "total", label: "TOTAL OVERHEADS", key: "totOverheads" },
  { type: "grand_total", label: "EBITDA", key: "ebitda" },

  {
    type: "row",
    label: "ETHOS/BEECHBROOK COSTS (Do not Use)",
    key: "ohEthosBeechbrook",
  },
  { type: "grand_total", label: "Adjusted EBITDA", key: "adjEbitda" },

  { type: "section", label: "EXCEPTIONAL ITEMS" },
  { type: "row", label: "ACCOUNTING FEES-EXCEPTIONAL", key: "excAccounting" },
  { type: "row", label: "LEGAL & PROFESSIONAL-EXCEPTIONAL", key: "excLegal" },
  { type: "row", label: "EXPENSES-EXCEPTIONAL", key: "excExpenses" },
  { type: "subtotal", label: "TOTAL EXCEPTIONAL ITEMS", key: "totExceptional" },
  {
    type: "total",
    label: "TOTAL OVERHEADS INCL. EXCEPTIONAL",
    key: "totOverheadsInclExc",
  },
  {
    type: "grand_total",
    label: "EBITDA INCL. EXCEPTIONALS",
    key: "ebitdaInclExc",
  },

  { type: "section", label: "FINANCE CHARGES" },
  { type: "row", label: "BANK CHARGES", key: "finBankCharges" },
  { type: "row", label: "BANK INTEREST PAID/(RECEIVABLE)", key: "finBankInt" },
  { type: "row", label: "FINANCE CHARGES", key: "finCharges" },
  { type: "row", label: "FOREIGH EXCHANGE GAIN/LOSSES", key: "finFx" },
  { type: "row", label: "LOAN INTEREST", key: "finLoanInt" },
  { type: "total", label: "TOTAL FINANCE CHARGES", key: "totFinanceCharges" },

  { type: "row", label: "DEPRECIATION AND AMORTISATION", key: "taxDepr" },
  { type: "row", label: "CORPORATION TAX", key: "taxCorp" },
  { type: "total", label: "TOTAL DEPRECIATION & TAX", key: "totDeprTax" },

  { type: "grand_total", label: "PROFIT/LOSS", key: "profit" },
];

const COLORS = {
  page: "#f4f6f8",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "#d9e0e7",
  borderStrong: "#c4ced8",
  text: "#1f2937",
  textMuted: "#64748b",
  textSoft: "#94a3b8",
  primary: "#2563eb",
  primaryDark: "#1d4ed8",
  primarySoft: "#eff6ff",
  success: "#15803d",
  successSoft: "#f0fdf4",
  danger: "#dc2626",
  dangerSoft: "#fef2f2",
  warning: "#b45309",
  warningSoft: "#fffbeb",
  header: "#eef2f6",
  total: "#f8fafc",
  grandTotal: "#eff6ff",
};

export default function SingleMonthPnlPage({
  globalFile,
  globalSheet,
  availableSheets: propSheets = [],
}) {
  const [localFile, setLocalFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [localSheets, setLocalSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [tbMode, setTbMode] = useState("auto");
  const [viewType, setViewType] = useState("analysis");
  const [displayFormat, setDisplayFormat] = useState("html");

  const sheets = propSheets.length > 0 ? propSheets : localSheets;
  const file = globalFile || localFile;
  const activeSheet = selectedSheet || globalSheet || (sheets.length > 0 ? sheets[0] : "");

  const periodGroups = useMemo(() => data?.periodGroups || [], [data?.periodGroups]);

  const activeYearGroup = useMemo(() => {
    if (!periodGroups.length) return null;

    const bySelected = periodGroups.find(
      (group) => String(group.year) === String(selectedYear),
    );

    if (bySelected) return bySelected;

    const byPeriod = periodGroups.find((group) => {
      if (selectedPeriod === group.fullYearLabel) return true;
      return group.months?.some((month) => month.label === selectedPeriod);
    });

    return byPeriod || periodGroups[0];
  }, [periodGroups, selectedYear, selectedPeriod]);

  const currentActiveYear = selectedYear || (activeYearGroup?.year !== undefined ? String(activeYearGroup.year) : "");

  const handleUpload = useCallback(
    async (
      selectedFile,
      periodOverride = "",
      modeOverride = "",
      sheetOverride = "",
    ) => {
      const fileToProcess = selectedFile || file;

      if (!fileToProcess) return;

      setLoading(true);
      setError(null);
      setCopied(false);

      const activeMode = modeOverride || tbMode;
      const targetSheet = sheetOverride !== "" ? sheetOverride : activeSheet;

      const formData = new FormData();

      formData.append("file", fileToProcess);
      formData.append("tbMode", activeMode);

      if (targetSheet) {
        formData.append("sheet", targetSheet);
      }

      if (periodOverride || selectedPeriod) {
        formData.append("period", periodOverride || selectedPeriod);
      }

      try {
        const res = await fetch("/api/pnl", {
          method: "POST",
          body: formData,
        });

        const json = await res.json();

        if (json.success) {
          setData(json.data);
          setSelectedPeriod(json.data.targetPeriod);

          if (json.data.sheetNames) {
            setLocalSheets(json.data.sheetNames);
          }

          if (!selectedSheet) {
            setSelectedSheet(json.data.activeSheetName || "");
          }

          const targetGroup = json.data.periodGroups?.find(
            (group) =>
              group.fullYearLabel === json.data.targetPeriod ||
              group.months?.some(
                (month) => month.label === json.data.targetPeriod,
              ),
          );

          if (targetGroup) {
            setSelectedYear(String(targetGroup.year));
          }
        } else {
          setError(json.error || "Failed to process Profit & Loss.");
        }
      } catch (err) {
        setError(err?.message || "An error occurred while uploading.");
      } finally {
        setLoading(false);
      }
    },
    [file, tbMode, activeSheet, selectedPeriod, selectedSheet],
  );

  useEffect(() => {
    let ignore = false;

    if (globalFile) {
      const sheetToUse = globalSheet || (sheets.length > 0 ? sheets[0] : "");
      const formData = new FormData();
      formData.append("file", globalFile);
      formData.append("tbMode", "auto");
      if (sheetToUse) formData.append("sheet", sheetToUse);

      fetch("/api/pnl", {
        method: "POST",
        body: formData,
      })
        .then((res) => res.json())
        .then((json) => {
          if (ignore) return;
          if (json.success) {
            setData(json.data);
            setSelectedPeriod(json.data.targetPeriod);
            if (json.data.sheetNames) {
              setLocalSheets(json.data.sheetNames);
            }
            if (json.data.activeSheetName) {
              setSelectedSheet(json.data.activeSheetName);
            }
            const targetGroup = json.data.periodGroups?.find(
              (group) =>
                group.fullYearLabel === json.data.targetPeriod ||
                group.months?.some(
                  (month) => month.label === json.data.targetPeriod,
                ),
            );
            if (targetGroup) {
              setSelectedYear(String(targetGroup.year));
            }
          }
        })
        .catch((err) => {
          if (!ignore) {
            console.warn("P&L auto-load error:", err);
          }
        });
    }

    return () => {
      ignore = true;
    };
  }, [globalFile, globalSheet, sheets]);

  const fmt = (num, isCount = false) => {
    if (num === undefined || num === null || isNaN(num)) return "0.00";

    if (isCount) {
      return Number(num).toLocaleString("en-GB", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    if (num < 0) {
      return `(${Math.abs(num).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })})`;
    }

    return num.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fmtPct = (num) => {
    if (num === undefined || num === null || isNaN(num) || num === 0) {
      return "0.0%";
    }

    if (num < 0) {
      return `(${Math.abs(num).toFixed(1)}%)`;
    }

    return `${num.toFixed(1)}%`;
  };

  const getVarColor = (num) => {
    if (!num || Math.abs(num) < 0.01) return COLORS.textSoft;
    return num > 0 ? COLORS.success : COLORS.danger;
  };

  const generateAnalysisPnlTsv = (report) => {
    if (!report || !report.analysisPl) return "";

    const analysisPl = report.analysisPl || {};
    const lines = [];

    lines.push(
      "MONTHLY INCOME\tACTUAL \tFORECAST\tBUDGET\tPRIOR YEAR\tVAR TO F'CAST\tVAR TO F'CAST (%)\tVAR TO BUDGET\tVAR TO BUDGET (%)\tVAR TO PRIOR YEAR\tVAR TO PRIOR YEAR (%)",
    );

    PNL_STRUCTURE.forEach((item) => {
      if (item.type === "section") {
        lines.push(`${item.label}\t\t\t\t\t\t\t\t\t\t`);
      } else {
        const r = analysisPl[item.key] || {
          actual: 0,
          forecast: 0,
          budget: 0,
          priorYear: 0,
          varF: 0,
          varFPct: 0,
          varB: 0,
          varBPct: 0,
          varP: 0,
          varPPct: 0,
        };

        lines.push(
          `${item.label}\t${fmt(r.actual, item.isCount)}\t${fmt(
            r.forecast,
            item.isCount,
          )}\t${fmt(r.budget, item.isCount)}\t${fmt(
            r.priorYear,
            item.isCount,
          )}\t${fmt(r.varF)}\t${fmtPct(r.varFPct)}\t${fmt(
            r.varB,
          )}\t${fmtPct(r.varBPct)}\t${fmt(r.varP)}\t${fmtPct(r.varPPct)}`,
        );

        if (item.type === "grand_total" || item.type === "total") {
          lines.push("");
        }
      }
    });

    return lines.join("\n");
  };

  const generateStandardPnlTsv = (report) => {
    if (!report || !report.pl) return "";

    const pl = report.pl || {};
    const lines = [];

    lines.push(
      `${report.isYearly ? "ANNUAL PROFIT & LOSS" : "MONTHLY INCOME"}\t${
        report.targetPeriod || ""
      }`,
    );

    lines.push("");

    PNL_STRUCTURE.forEach((item) => {
      if (item.type === "section") {
        lines.push(`${item.label}`);
      } else {
        const val = pl[item.key] || 0;

        lines.push(`${item.label}\t${fmt(val, item.isCount)}`);

        if (item.type === "grand_total" || item.type === "total") {
          lines.push("");
        }
      }
    });

    return lines.join("\n");
  };

  const handlePeriodSelect = (newPeriod, year = "") => {
    if (newPeriod === selectedPeriod) return;

    if (year !== "") {
      setSelectedYear(String(year));
    }

    setSelectedPeriod(newPeriod);

    if (file) {
      handleUpload(file, newPeriod, tbMode, activeSheet);
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYear(String(year));
  };

  const handleModeChange = (newMode) => {
    setTbMode(newMode);

    if (file) {
      handleUpload(file, selectedPeriod, newMode, activeSheet);
    }
  };

  const handleSheetChange = (e) => {
    const newSheet = e.target.value;

    setSelectedSheet(newSheet);

    if (file) {
      handleUpload(file, selectedPeriod, tbMode, newSheet);
    }
  };

  const copyToClipboard = async () => {
    if (!data) return;

    const textToCopy =
      viewType === "analysis"
        ? generateAnalysisPnlTsv(data)
        : generateStandardPnlTsv(data);

    try {
      await navigator.clipboard.writeText(textToCopy);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      setError("Unable to copy the report to clipboard.");
    }
  };

  const controlButtonStyle = (active, variant = "primary") => {
    const activeBg =
      variant === "success"
        ? COLORS.success
        : variant === "indigo"
          ? "#4f46e5"
          : COLORS.primary;

    return {
      padding: "7px 11px",
      borderRadius: "6px",
      border: `1px solid ${active ? activeBg : COLORS.border}`,
      background: active ? activeBg : COLORS.surface,
      color: active ? "#ffffff" : COLORS.textMuted,
      fontSize: "12px",
      fontWeight: active ? 600 : 500,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "all 0.15s ease",
    };
  };

  const labelStyle = {
    display: "block",
    marginBottom: "5px",
    fontSize: "11px",
    fontWeight: 600,
    color: COLORS.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: COLORS.page,
        color: COLORS.text,
        fontFamily:
          'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        padding: "20px",
      }}
    >
      <div
        style={{
          maxWidth: "1500px",
          margin: "0 auto",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: "20px",
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "22px",
                fontWeight: 700,
                color: COLORS.text,
                letterSpacing: "-0.02em",
                marginBottom: "4px",
              }}
            >
              P&amp;L Analysis
            </div>

            <div
              style={{
                fontSize: "12px",
                color: COLORS.textMuted,
              }}
            >
              Universal auto-extracting Profit &amp; Loss analysis and
              Excel-ready reporting.
            </div>
          </div>

          {data && (
            <button
              onClick={copyToClipboard}
              style={{
                padding: "8px 14px",
                borderRadius: "7px",
                border: `1px solid ${copied ? "#86efac" : COLORS.primary}`,
                background: copied ? COLORS.successSoft : COLORS.surface,
                color: copied ? COLORS.success : COLORS.primary,
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {copied
                ? "Copied"
                : `Copy ${
                    viewType === "analysis" ? "Analysis P&L" : "Standard P&L"
                  }`}
            </button>
          )}
        </div>

        {/* CONTROLS */}
        <div
          style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: "10px",
            padding: "14px",
            marginBottom: "12px",
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "14px",
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {!globalFile && (
              <div>
                <label style={labelStyle}>Excel TB File</label>

                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  id="tb-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];

                    if (f) {
                      setLocalFile(f);
                      handleUpload(f);
                    }
                  }}
                  style={{
                    fontSize: "12px",
                    color: COLORS.text,
                    maxWidth: "240px",
                  }}
                />
              </div>
            )}

            {sheets.length > 0 && (
              <div>
                <label style={labelStyle}>Sheet</label>

                <select
                  value={activeSheet}
                  onChange={handleSheetChange}
                  style={{
                    minWidth: "160px",
                    padding: "7px 10px",
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "6px",
                    color:
                      activeSheet === globalSheet
                        ? COLORS.primary
                        : COLORS.warning,
                    fontSize: "12px",
                    outline: "none",
                  }}
                >
                  {sheets.map((sheet, index) => (
                    <option key={`${sheet}-${index}`} value={sheet}>
                      {sheet}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label style={labelStyle}>TB Detection</label>

              <div
                style={{
                  display: "flex",
                  gap: "5px",
                }}
              >
                {[
                  {
                    id: "auto",
                    label: `Auto${
                      data
                        ? ` (${
                            data.detectedMode === "cumulative"
                              ? "YTD"
                              : "Periodic"
                          })`
                        : ""
                    }`,
                  },
                  {
                    id: "cumulative",
                    label: "Cumulative YTD",
                  },
                  {
                    id: "periodic",
                    label: "Periodic Delta",
                  },
                ].map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => handleModeChange(mode.id)}
                    disabled={loading}
                    style={controlButtonStyle(tbMode === mode.id, "primary")}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={labelStyle}>Report Type</label>

              <div
                style={{
                  display: "flex",
                  gap: "5px",
                }}
              >
                <button
                  onClick={() => setViewType("analysis")}
                  style={controlButtonStyle(viewType === "analysis", "success")}
                >
                  Analysis P&amp;L
                </button>

                <button
                  onClick={() => setViewType("standard")}
                  style={controlButtonStyle(viewType === "standard")}
                >
                  Standard P&amp;L
                </button>
              </div>
            </div>

            <div
              style={{
                marginLeft: "auto",
              }}
            >
              <label style={labelStyle}>Display</label>

              <div
                style={{
                  display: "flex",
                  gap: "5px",
                }}
              >
                <button
                  onClick={() => setDisplayFormat("html")}
                  style={controlButtonStyle(displayFormat === "html", "indigo")}
                >
                  Table
                </button>

                <button
                  onClick={() => setDisplayFormat("txt")}
                  style={controlButtonStyle(displayFormat === "txt", "indigo")}
                >
                  Raw TSV
                </button>
              </div>
            </div>
          </div>

          {loading && (
            <div
              style={{
                marginTop: "12px",
                padding: "8px 10px",
                borderRadius: "6px",
                background: COLORS.primarySoft,
                color: COLORS.primary,
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              Calculating periods and variances...
            </div>
          )}
        </div>

        {/* COMPACT PERIOD NAVIGATION */}
        {periodGroups.length > 0 && (
          <div
            style={{
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: "10px",
              marginBottom: "12px",
              overflow: "hidden",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            }}
          >
            {/* YEAR TABS */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "8px",
                borderBottom: `1px solid ${COLORS.border}`,
                background: COLORS.surfaceAlt,
                overflowX: "auto",
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  padding: "0 8px",
                  fontSize: "10px",
                  fontWeight: 700,
                  color: COLORS.textSoft,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Year
              </div>

              {periodGroups.map((group, index) => {
                const active =
                  String(group.year) === String(currentActiveYear);

                return (
                  <button
                    key={`${group.year}-${index}`}
                    onClick={() => handleYearSelect(group.year)}
                    disabled={loading}
                    style={{
                      flexShrink: 0,
                      minWidth: "72px",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: `1px solid ${
                        active ? COLORS.primary : COLORS.border
                      }`,
                      background: active ? COLORS.primary : COLORS.surface,
                      color: active ? "#ffffff" : COLORS.textMuted,
                      fontSize: "12px",
                      fontWeight: active ? 700 : 500,
                      cursor: loading ? "not-allowed" : "pointer",
                    }}
                  >
                    {group.year === "General" ? "General" : group.year}
                  </button>
                );
              })}
            </div>

            {/* MONTHS FOR SELECTED YEAR */}
            {activeYearGroup && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "9px 10px",
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                <button
                  onClick={() =>
                    handlePeriodSelect(
                      activeYearGroup.fullYearLabel,
                      activeYearGroup.year,
                    )
                  }
                  disabled={loading}
                  style={{
                    flexShrink: 0,
                    padding: "6px 11px",
                    borderRadius: "6px",
                    border: `1px solid ${
                      selectedPeriod === activeYearGroup.fullYearLabel
                        ? COLORS.primary
                        : "#93c5fd"
                    }`,
                    background:
                      selectedPeriod === activeYearGroup.fullYearLabel
                        ? COLORS.primary
                        : COLORS.primarySoft,
                    color:
                      selectedPeriod === activeYearGroup.fullYearLabel
                        ? "#ffffff"
                        : COLORS.primary,
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  FY
                </button>

                <div
                  style={{
                    width: "1px",
                    height: "22px",
                    background: COLORS.border,
                    margin: "0 2px",
                    flexShrink: 0,
                  }}
                />

                {activeYearGroup.months?.map((month, index) => {
                  const active = selectedPeriod === month.label;

                  return (
                    <button
                      key={`${month.label}-${index}`}
                      onClick={() =>
                        handlePeriodSelect(month.label, activeYearGroup.year)
                      }
                      disabled={loading}
                      style={{
                        flexShrink: 0,
                        minWidth: "56px",
                        padding: "6px 9px",
                        borderRadius: "6px",
                        border: `1px solid ${
                          active ? COLORS.primary : COLORS.border
                        }`,
                        background: active
                          ? COLORS.primarySoft
                          : COLORS.surface,
                        color: active ? COLORS.primaryDark : COLORS.textMuted,
                        fontSize: "11px",
                        fontWeight: 700,
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                    >
                      {month.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ERROR */}
        {error && (
          <div
            style={{
              background: COLORS.dangerSoft,
              border: "1px solid #fecaca",
              color: COLORS.danger,
              padding: "10px 12px",
              borderRadius: "7px",
              marginBottom: "12px",
              fontSize: "12px",
            }}
          >
            {error}
          </div>
        )}

        {/* ACTIVE REPORT */}
        {data && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
                marginBottom: "8px",
                fontSize: "11px",
                color: COLORS.textMuted,
              }}
            >
              <span>Active period:</span>

              <span
                style={{
                  padding: "3px 7px",
                  borderRadius: "5px",
                  background: COLORS.primarySoft,
                  color: COLORS.primary,
                  fontWeight: 700,
                }}
              >
                {data.targetPeriod}
              </span>

              <span>•</span>

              <span>
                {viewType === "analysis"
                  ? "Analysis P&L · 11 columns"
                  : "Standard P&L"}
              </span>
            </div>

            {displayFormat === "html" ? (
              <div
                style={{
                  background: COLORS.surface,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "10px",
                  overflow: "auto",
                  maxHeight: "720px",
                  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    minWidth: viewType === "analysis" ? "1250px" : "650px",
                    borderCollapse: "collapse",
                    fontSize: "11px",
                    lineHeight: "1.4",
                  }}
                >
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      background: COLORS.header,
                      borderBottom: `2px solid ${COLORS.borderStrong}`,
                      zIndex: 10,
                    }}
                  >
                    {viewType === "analysis" ? (
                      <tr>
                        <th
                          style={{
                            padding: "10px 14px",
                            textAlign: "left",
                            minWidth: "300px",
                            color: COLORS.text,
                            fontWeight: 700,
                          }}
                        >
                          LINE ITEM
                        </th>

                        {[
                          ["ACTUAL", COLORS.primary],
                          ["FORECAST", COLORS.textMuted],
                          ["BUDGET", COLORS.textMuted],
                          ["PRIOR YEAR", COLORS.textMuted],
                          ["VAR FCST", COLORS.textMuted],
                          ["VAR %", COLORS.textMuted],
                          ["VAR BGT", COLORS.textMuted],
                          ["VAR %", COLORS.textMuted],
                          ["VAR PY", COLORS.textMuted],
                          ["VAR %", COLORS.textMuted],
                        ].map(([label, color], index) => (
                          <th
                            key={`${label}-${index}`}
                            style={{
                              padding: "10px 10px",
                              textAlign: "right",
                              minWidth: label === "VAR %" ? "70px" : "90px",
                              color,
                              fontWeight: 700,
                            }}
                          >
                            {label}
                          </th>
                        ))}
                      </tr>
                    ) : (
                      <tr>
                        <th
                          style={{
                            padding: "10px 14px",
                            textAlign: "left",
                            color: COLORS.text,
                            fontWeight: 700,
                          }}
                        >
                          LINE ITEM
                        </th>

                        <th
                          style={{
                            padding: "10px 14px",
                            textAlign: "right",
                            width: "180px",
                            color: COLORS.primary,
                            fontWeight: 700,
                          }}
                        >
                          AMOUNT ({data.targetPeriod})
                        </th>
                      </tr>
                    )}
                  </thead>

                  <tbody>
                    {PNL_STRUCTURE.map((item, idx) => {
                      if (item.type === "section") {
                        return (
                          <tr
                            key={idx}
                            style={{
                              background: COLORS.surfaceAlt,
                              borderTop: `1px solid ${COLORS.border}`,
                              borderBottom: `1px solid ${COLORS.border}`,
                            }}
                          >
                            <td
                              colSpan={viewType === "analysis" ? 11 : 2}
                              style={{
                                padding: "10px 14px",
                                fontWeight: 700,
                                color: COLORS.primaryDark,
                                letterSpacing: "0.04em",
                                fontSize: "10px",
                              }}
                            >
                              {item.label}
                            </td>
                          </tr>
                        );
                      }

                      const isGrandTotal = item.type === "grand_total";
                      const isTotal = item.type === "total";
                      const isSubtotal = item.type === "subtotal";
                      const isMetric = item.type === "metric";

                      const rowBackground = isGrandTotal
                        ? COLORS.grandTotal
                        : isTotal
                          ? COLORS.total
                          : isSubtotal
                            ? "#fafbfc"
                            : idx % 2 === 0
                              ? COLORS.surface
                              : COLORS.surfaceAlt;

                      const rowStyle = {
                        background: rowBackground,
                        borderTop: isGrandTotal
                          ? `2px solid ${COLORS.primary}`
                          : isTotal
                            ? `1px solid ${COLORS.borderStrong}`
                            : isSubtotal
                              ? `1px solid ${COLORS.border}`
                              : "none",
                        borderBottom: isGrandTotal
                          ? `2px solid ${COLORS.primary}`
                          : "none",
                        fontWeight:
                          isGrandTotal || isTotal
                            ? 700
                            : isSubtotal
                              ? 600
                              : 400,
                      };

                      if (viewType === "analysis") {
                        const r = data.analysisPl?.[item.key] || {
                          actual: 0,
                          forecast: 0,
                          budget: 0,
                          priorYear: 0,
                          varF: 0,
                          varFPct: 0,
                          varB: 0,
                          varBPct: 0,
                          varP: 0,
                          varPPct: 0,
                        };

                        return (
                          <tr key={idx} style={rowStyle}>
                            <td
                              style={{
                                padding: "7px 14px",
                                paddingLeft:
                                  isMetric ||
                                  isSubtotal ||
                                  isTotal ||
                                  isGrandTotal
                                    ? "14px"
                                    : "28px",
                                color: isGrandTotal
                                  ? COLORS.primaryDark
                                  : COLORS.text,
                              }}
                            >
                              {item.label}
                            </td>

                            <td
                              style={{
                                padding: "7px 10px",
                                textAlign: "right",
                                color: isGrandTotal
                                  ? COLORS.primaryDark
                                  : COLORS.text,
                              }}
                            >
                              {fmt(r.actual, item.isCount)}
                            </td>

                            <td
                              style={{
                                padding: "7px 10px",
                                textAlign: "right",
                                color: COLORS.textMuted,
                              }}
                            >
                              {fmt(r.forecast, item.isCount)}
                            </td>

                            <td
                              style={{
                                padding: "7px 10px",
                                textAlign: "right",
                                color: COLORS.textMuted,
                              }}
                            >
                              {fmt(r.budget, item.isCount)}
                            </td>

                            <td
                              style={{
                                padding: "7px 10px",
                                textAlign: "right",
                                color: COLORS.textMuted,
                              }}
                            >
                              {fmt(r.priorYear, item.isCount)}
                            </td>

                            <td
                              style={{
                                padding: "7px 10px",
                                textAlign: "right",
                                color: getVarColor(r.varF),
                              }}
                            >
                              {fmt(r.varF)}
                            </td>

                            <td
                              style={{
                                padding: "7px 10px",
                                textAlign: "right",
                                color: getVarColor(r.varFPct),
                              }}
                            >
                              {fmtPct(r.varFPct)}
                            </td>

                            <td
                              style={{
                                padding: "7px 10px",
                                textAlign: "right",
                                color: getVarColor(r.varB),
                              }}
                            >
                              {fmt(r.varB)}
                            </td>

                            <td
                              style={{
                                padding: "7px 10px",
                                textAlign: "right",
                                color: getVarColor(r.varBPct),
                              }}
                            >
                              {fmtPct(r.varBPct)}
                            </td>

                            <td
                              style={{
                                padding: "7px 10px",
                                textAlign: "right",
                                color: getVarColor(r.varP),
                              }}
                            >
                              {fmt(r.varP)}
                            </td>

                            <td
                              style={{
                                padding: "7px 10px",
                                textAlign: "right",
                                color: getVarColor(r.varPPct),
                              }}
                            >
                              {fmtPct(r.varPPct)}
                            </td>
                          </tr>
                        );
                      }

                      const val = data.pl?.[item.key] || 0;

                      return (
                        <tr key={idx} style={rowStyle}>
                          <td
                            style={{
                              padding: "7px 14px",
                              paddingLeft:
                                isMetric ||
                                isSubtotal ||
                                isTotal ||
                                isGrandTotal
                                  ? "14px"
                                  : "28px",
                              color: isGrandTotal
                                ? COLORS.primaryDark
                                : COLORS.text,
                            }}
                          >
                            {item.label}
                          </td>

                          <td
                            style={{
                              padding: "7px 14px",
                              textAlign: "right",
                              color: isGrandTotal
                                ? COLORS.primaryDark
                                : COLORS.text,
                            }}
                          >
                            {fmt(val, item.isCount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <textarea
                readOnly
                value={
                  viewType === "analysis"
                    ? generateAnalysisPnlTsv(data)
                    : generateStandardPnlTsv(data)
                }
                rows={48}
                style={{
                  width: "100%",
                  background: COLORS.surface,
                  color: COLORS.text,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "10px",
                  padding: "16px",
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: "11px",
                  lineHeight: "1.5",
                  whiteSpace: "pre",
                  boxSizing: "border-box",
                  outline: "none",
                  resize: "vertical",
                }}
                onFocus={(e) => e.target.select()}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}